import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useHydra } from './hooks/useHydra';
import { useKalshiAccount } from './hooks/useKalshiAccount';
import { usePolyAccount } from './hooks/usePolyAccount';
import { playBootTone } from './lib/audio/defenseAudio';
import { readSoundSettings } from './lib/audio/soundSettings';
import { useFeeStore } from './services/hydraFees';
import { useAuthStore } from './stores/authStore';
import { LoginView } from './views/Login';
import { AdminDashboard } from './views/Admin';
import { ManagerDashboard } from './views/Manager';
import { AuthShell } from './components/layout/AuthShell';
import { ValueDashboard } from './views/ValueDashboard';
import { PositionTracker } from './views/PositionTracker';
import { TraderDetail } from './views/TraderDetail';
import { OrgDetail } from './views/OrgDetail';
import { Settings } from './views/Settings';
import { StreamRiftView } from './views/StreamRift';
import { MyTrades } from './views/MyTrades';
import { KeyModeBanner } from './components/KeyModeBanner';
import {
  TRADER_NAV_ITEMS,
  ADMIN_NAV_ITEMS,
  MANAGER_NAV_ITEMS,
  type TraderView,
  type AdminView,
  type ManagerView,
} from './types/navigation';
import { DetailNavProvider, useDetailNav } from './contexts/DetailNavContext';
import { Loader2 } from 'lucide-react';

const G3View = lazy(() => import('./views/G3'));
const G4View = lazy(() => import('./views/G4'));

function App() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const checkBootstrap = useAuthStore((s) => s.checkBootstrap);

  useEffect(() => {
    checkBootstrap();
  }, [checkBootstrap]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="bg-background text-muted-foreground flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (status === 'unauthenticated' || !user) {
    return <LoginView />;
  }

  switch (user.role) {
    case 'admin':
      return <AdminShell />;
    case 'manager':
      return <ManagerShell />;
    case 'trader':
      return <TraderShell />;
    default:
      return <LoginView />;
  }
}

// ── Shared trader view renderer ──────────────────────────────────

function TraderViews({
  currentView,
  onViewChange,
}: {
  currentView: string;
  onViewChange?: (view: TraderView) => void;
}) {
  const { selectTrader, selectOrg } = useDetailNav();
  return (
    <div className="flex h-full flex-col">
      <KeyModeBanner />
      <div className="min-h-0 flex-1">
        {currentView === 'dashboard' && (
          <ValueDashboard
            onOpenPositionsPage={onViewChange ? () => onViewChange('positions') : undefined}
          />
        )}
        {currentView === 'positions' && (
          <PositionTracker
            onSelectTrader={(row) =>
              selectTrader({ userId: row.userId, username: row.username })
            }
            onSelectOrg={(row) => selectOrg({ orgId: row.orgId, orgName: row.name })}
          />
        )}
        {currentView === 'trades' && <MyTrades />}
        {currentView === 'settings' && <Settings />}
        {currentView === 'streamrift' && <StreamRiftView />}
        {currentView === 'g3' && (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
                Loading G3…
              </div>
            }
          >
            <G3View />
          </Suspense>
        )}
        {currentView === 'g4' && (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
                Loading G4…
              </div>
            }
          >
            <G4View />
          </Suspense>
        )}
      </div>
    </div>
  );
}

// ── Detail overlay — replaces main pane content when selected ───

function DetailOrChildren({ children }: { children: React.ReactNode }) {
  const { selected, selectTrader, clear } = useDetailNav();
  if (selected?.type === 'trader') {
    return (
      <TraderDetail
        traderId={selected.userId}
        username={selected.username}
        orgId={selected.orgId}
        onBack={clear}
      />
    );
  }
  if (selected?.type === 'org') {
    return (
      <OrgDetail
        orgId={selected.orgId}
        orgName={selected.orgName}
        onBack={clear}
        onTraderClick={(row) =>
          selectTrader({ userId: row.userId, username: row.username, orgId: selected.orgId })
        }
      />
    );
  }
  return <>{children}</>;
}

function useBootSound() {
  const bootPlayed = useRef(false);
  useEffect(() => {
    if (!bootPlayed.current) {
      bootPlayed.current = true;
      const snd = readSoundSettings();
      if (snd.bootSoundEnabled) playBootTone();
    }
  }, []);
}

// Fetch the per-series Kalshi fee schedule once per session — see services/hydraFees.
function useFeeBootstrap() {
  useEffect(() => {
    useFeeStore.getState().loadFees().catch((err) => {
      console.warn('[hydraFees] initial load failed:', err);
    });
  }, []);
}

const TRADER_VIEW_IDS = new Set(TRADER_NAV_ITEMS.map((i) => i.id));

// ── Admin Shell (onion: admin + manager + trader) ────────────────

function AdminShell() {
  return (
    <DetailNavProvider>
      <AdminShellInner />
    </DetailNavProvider>
  );
}

function AdminShellInner() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  useFeeBootstrap();
  const [currentView, setCurrentView] = useState<AdminView>('dashboard');
  const { clear } = useDetailNav();
  // Sidebar nav also exits any open detail page.
  useEffect(() => { clear(); }, [currentView, clear]);

  return (
    <AuthShell
      navItems={ADMIN_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
      brandLetter="A"
    >
      <DetailOrChildren>
        {currentView === 'admin-manage' && <AdminDashboard />}
        {currentView === 'admin-traders' && (
          <ManagerDashboard currentView="manager-traders" adminMode />
        )}
        {TRADER_VIEW_IDS.has(currentView as TraderView) && (
          <TraderViews
            currentView={currentView}
            onViewChange={(v) => setCurrentView(v)}
          />
        )}
      </DetailOrChildren>
    </AuthShell>
  );
}

// ── Manager Shell (onion: manager + trader) ──────────────────────

function ManagerShell() {
  return (
    <DetailNavProvider>
      <ManagerShellInner />
    </DetailNavProvider>
  );
}

function ManagerShellInner() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  useFeeBootstrap();
  const [currentView, setCurrentView] = useState<ManagerView>('dashboard');
  const { clear } = useDetailNav();
  useEffect(() => { clear(); }, [currentView, clear]);

  return (
    <AuthShell
      navItems={MANAGER_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
      brandLetter="M"
    >
      <DetailOrChildren>
        {currentView === 'manager-traders' && <ManagerDashboard currentView="manager-traders" />}
        {TRADER_VIEW_IDS.has(currentView as TraderView) && (
          <TraderViews
            currentView={currentView}
            onViewChange={(v) => setCurrentView(v)}
          />
        )}
      </DetailOrChildren>
    </AuthShell>
  );
}

// ── Trader Shell ─────────────────────────────────────────────────

function TraderShell() {
  return (
    <DetailNavProvider>
      <TraderShellInner />
    </DetailNavProvider>
  );
}

function TraderShellInner() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  useFeeBootstrap();
  const [currentView, setCurrentView] = useState<TraderView>('dashboard');
  const { clear } = useDetailNav();
  useEffect(() => { clear(); }, [currentView, clear]);

  return (
    <AuthShell
      navItems={TRADER_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
    >
      <DetailOrChildren>
        <TraderViews currentView={currentView} onViewChange={setCurrentView} />
      </DetailOrChildren>
    </AuthShell>
  );
}

export default App;
